import { Box, Grid, IconButton, Typography } from "@mui/material";
import ControlledSelect from "components/Controller/ControlledSelect";
import ControlledTextField from "components/Controller/ControlledTextField";
import CustomizedMenuOptions from "components/Custom/CustomizedMenuOptions";
import CustomizedStatus from "components/Custom/CustomizedStatus";
import { CustomizedTooltip } from "components/Custom/CustomizedTooltip";
import EmployeeListForm from "components/Form/EmployeeList";
import Confirmation from "components/UI/Confirmation";
import { useDeliveryTripOption } from "hooks/Logistic/DeliveryTrip/use-delivery-trip-option";
import { useConfirmation } from "hooks/use-confirmation";
import { useDisable } from "hooks/use-disable";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { IMenuOption } from "types/global";
import { IDeliveryTrip } from "types/Logistic/deliveryTrip";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import ControlledDatePicker from "components/Controller/ControlledDatePicker";
import { createGraphQLClientWithMiddleware } from "services/graphqlClient";
import {
  ActivityLogDocumentType,
  ActivityType,
  DeliveryTripFindUniqueQuery,
  DeliveryTripUpdateInput,
  SalesModelType,
  SalesUniqueIdGenerateQuery,
  useDeliveryTripUpdateMutation,
  useSalesUniqueIdGenerateQuery,
} from "generated/sales";
import { useCallback, useEffect } from "react";
import {
  copyDeliveryTripHandler,
  createMaintenanceFromDeliveryTrip,
  deliveryTripUpdatePayload,
} from "utils/Formatter/Logistic/DeliveryTrip";
import { useStateContext } from "contexts/auth-context";
import CancelConfirmation from "components/UI/CancelConfirmation";
import PanoramaFishEyeIcon from "@mui/icons-material/PanoramaFishEye";
import CustomizedChips from "components/Custom/CustomizedChips";
import { VEHICLE_VIEW } from "services/AgGrid/VehicleAgGrid";
import { useModal } from "hooks/use-modal";
import AcceptModal from "components/Form/Sales/Accept/AcceptModal";
import { useActivityLog } from "hooks/use-activity-log";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { RefetchOptions, RefetchQueryFilters, QueryObserverResult } from "@tanstack/react-query";

type Props = {
  editClickHandler: () => void;
  cancelHandler?: () => void;
  isEdit: boolean;
  refetch: <TPageData>(options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined) => Promise<QueryObserverResult<DeliveryTripFindUniqueQuery, unknown>>
  isCancellable?: boolean;
};

const DeliveryTripHeader = ({
  cancelHandler,
  editClickHandler,
  isEdit,
  refetch,
  isCancellable,
}: Props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const {
    control,
    formState: { errors },
    getValues,
    setValue,
    watch,
    setError,
  } = useFormContext<IDeliveryTrip>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const uniqueId = watch("unique_id");

  const {
    state: { authUser, permissions },
  } = useStateContext();

  const [disabled] = useDisable();

  const mainStatus = watch("main_status");
  const aggrid_status = useWatch({
    control,
    name: "aggrid_status",
  });
  const flagStatus = watch("flag_status");
  const issueDate = useWatch({
    control,
    name: "issue_date",
  });

  const late = flagStatus.includes("late");
  const cancelled = flagStatus.includes("cancelled");

  const status = late && !cancelled ? mainStatus : aggrid_status;

  const printOptions: IMenuOption[] = [
    {
      value: `Daily Worksheet`,
      disabled: !id,
    },
  ];

  // 86d4889jb — 4 values in workflow order. There are no transition rules:
  // any value can move to any other, and `saveHandler` writes a StatusChange
  // activity log on every change regardless of direction.
  const statusDropdownOptions = [
    { label: t("status.booking"), value: "booking", disabled: false },
    { label: t("status.wait_deliver"), value: "wait_deliver", disabled: false },
    { label: t("status.delivering"), value: "delivering", disabled: false },
    { label: t("status.finished"), value: "finished", disabled: false },
  ];

  const copyDocumentHandler = () => {
    const drliveryTrip = getValues();
    const formatDeliveryTrip = copyDeliveryTripHandler(drliveryTrip, authUser);
    navigate("/logistic/delivery-trip/add", {
      state: formatDeliveryTrip,
    });
  };

  const graphQLClient = createGraphQLClientWithMiddleware("sales");
  const { mutateAsync: update } = useDeliveryTripUpdateMutation<Error>(graphQLClient);
  const { createActivityLog } = useActivityLog();

  const { refetch: refetchUniqueId } =
    useSalesUniqueIdGenerateQuery<SalesUniqueIdGenerateQuery>(
      graphQLClient,
      {
        modelType: SalesModelType.DeliveryTrip,
        issueDate: issueDate ? dayjs(issueDate).format("YYYY-MM-DD") : undefined,
      },
      {
        enabled: false,
      }
    );

  const generateUniqueId = useCallback(async () => {
    const { data } = await refetchUniqueId({
      modelType: SalesModelType.DeliveryTrip,
      issueDate: issueDate ? dayjs(issueDate).format("YYYY-MM-DD") : undefined,
    } as any);
    setValue("unique_id", data?.UniqueIdGenerate ?? "");
  }, [refetchUniqueId, setValue, issueDate]);

  useEffect(() => {
    if (!id) {
      generateUniqueId();
    }
  }, [generateUniqueId, id]);

  const createMaintenance = async () => {
    const deliveryTrip = getValues();
    const { VehicleFindViewAggrid: vehicles } = await graphQLClient.request(
      VEHICLE_VIEW,
      {
        aggridInput: {
          filterModel: {
            id: {
              values: [deliveryTrip.vehicle_id, deliveryTrip.trailer_id].filter(
                Boolean
              ),
              filterType: "set",
            },
          },
        },
      }
    );
    const vehicle_details = vehicles.results?.find(
      (vehicle: any) => vehicle.id === deliveryTrip.vehicle_id
    );
    const trailer_details = vehicles.results?.find(
      (vehicle: any) => vehicle.id === deliveryTrip.trailer_id
    );
    navigate("/logistic/maintenance/add", {
      state: createMaintenanceFromDeliveryTrip({
        ...deliveryTrip,
        vehicle: vehicle_details ?? deliveryTrip.vehicle,
        trailer: trailer_details ?? deliveryTrip.trailer,
      }),
    });
  };

  const {
    confirmation: copyConfirmation,
    openConfirmationHandler: openCopyConfirmation,
    closeConfirmationHandler: closeCopyConfirmation,
    submitConfirmationHandler: submitCopyConfirmation,
  } = useConfirmation(copyDocumentHandler);

  const {
    confirmation: cancelConfirmation,
    openConfirmationHandler: openCancelConfirmation,
    closeConfirmationHandler: closeCancelConfirmation,
    submitConfirmationHandler: submitCancelConfirmation,
  } = useConfirmation(cancelHandler);

  const paymentHandler = async () => {
    const data = getValues();
    if (data.payment_date && data.payment_date.isValid && data.payment_date.isValid()) {
      const paymentDateString = data.payment_date.toISOString();
      data.payment_date = dayjs(paymentDateString);
    } else {
      enqueueSnackbar("กรุณาระบุวันที่จ่ายเงินที่ถูกต้อง", {
        variant: "error",
      });
      return;
    }

    // 86d37db3t: บันทึกวันจ่าย no longer flips payment_status or locks the
    // document — save whatever main_status the form currently holds and only
    // persist payment_date/payment_detail. List-view paid flag now derives
    // from payment_date presence.
    const formatData = await deliveryTripUpdatePayload(data, data.main_status);

    const { DeliveryTripUpdate } = await update({
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
      updateInput: formatData as DeliveryTripUpdateInput,
    });

    await refetch();
    enqueueSnackbar(`${t("logistic.delivery_trip.payment_date.success")}`, {
      variant: "success",
    });

    await createActivityLog({
      activity_type: ActivityType.SaveDatePayment,
      document_type: ActivityLogDocumentType.DeliveryTrip,
      reference_id: DeliveryTripUpdate?.id || 0,
      activity_detail: {
        secondary_operation: ActivityType.SaveDatePayment,
      },
    });
  }

  const validatePaymentHandler = () => {
    const payment_date = getValues("payment_date");
    if (!payment_date) {
      setError("payment_date", { message: "กรุณาระบุ" });
      enqueueSnackbar(`${t("logistic.delivery_trip.payment_date.error")}`, {
      variant: "error",
      });
      return;
    }
    submitPaymentModalHandler();
  };

  const {
    modal: paymentModal,
    openModalHandler: openPaymentModalHandler,
    closeModalHandler: closePaymentModalHandler,
    submitModalHandler: submitPaymentModalHandler,
  } = useModal(paymentHandler);

  const selectModifyOptions = useDeliveryTripOption(
    status,
    isEdit,
    permissions?.delivery_trip,
    uniqueId,
    permissions?.maintenance,
  );

  // const disabledStatus = ["delivering", "finished"].includes(status);

  const reference_document_list = useWatch({
    control,
    name: "reference_document_list",
  });

  const filteredRefDocument = reference_document_list?.filter(
    (ref) => !ref.hidden
  );

  return (
    <Box maxWidth={1040} mt={status === "finished" ? 0 : 2}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="h5">
              {t("logistic.delivery_trip.index")}
            </Typography>
            {/* Badge on the left as soon as the doc exists — shown in View
                and Edit mode. Reads `main_status` (frozen: dropdown writes to
                `new_main_status` instead, so `main_status` only flips after
                save + refetch reloads the form). Hidden in create mode. */}
            {(id || cancelled) && <CustomizedStatus status={status} />}
            {late && (
              <Box display={"flex"} gap={0.5} alignItems={"center"}>
                <PanoramaFishEyeIcon
                  sx={{
                    color: "#E41B1B",
                    bgcolor: "#FCE8E8",
                    fontSize: "10px",
                    borderRadius: 5,
                  }}
                />
                <Typography sx={{ color: "#E41B1B" }}>
                  {t("status.late")}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>
        <Grid item sm={12} md={6}>
          <Grid container spacing={1.5} justifyContent="end" alignItems="center">
            {(!id || isEdit) && !cancelled && (
              <Grid item sm={5.5} md={4}>
                <ControlledSelect
                  control={control}
                  name="new_main_status"
                  label={t("status.index")}
                  options={statusDropdownOptions}
                  error={Boolean(errors?.new_main_status)}
                  helperText={errors?.new_main_status?.message?.toString()}
                  required
                />
              </Grid>
            )}
            {id && permissions?.delivery_trip?.view && !cancelled && (
              <Grid item sm={5.5} md={4}>
                <CustomizedMenuOptions
                  fullWidth
                  size="medium"
                  label={"พิมพ์เอกสาร"}
                  options={printOptions}
                  onSelect={(e) => {
                    const value = e.target as HTMLElement;
                    if (value.innerText === `Daily Worksheet`) {
                      navigate(`/logistic/delivery-trip/${id}/pdf`);
                    }
                  }}
                />
              </Grid>
            )}
            {id && selectModifyOptions?.length > 0 && (
              <Grid item sm={5.5} md={4}>
                <CustomizedMenuOptions
                  fullWidth
                  size="medium"
                  label={"ตัวเลือก"}
                  options={selectModifyOptions}
                  onSelect={(e) => {
                    const value = e.target as HTMLElement;
                    switch (value.innerText) {
                      case "แก้ไข":
                        editClickHandler();
                        break;
                      case "คัดลอก":
                        openCopyConfirmation();
                        break;
                      case "ยกเลิก":
                        openCancelConfirmation();
                        break;
                      case "บันทึกวันจ่าย":
                        openPaymentModalHandler();
                        break;
                      case "สร้างใบแจ้งซ่อม":
                        createMaintenance();
                        break;
                      default:
                        break;
                    }
                  }}
                  disabled={!id}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={1.5}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} sm={12} md={4}>
              <Typography fontWeight={600}>
                {t("logistic.delivery_trip.unique_id")}
              </Typography>
            </Grid>
            <Grid item xs={11} sm={11} md={7}>
              <ControlledTextField
                name="unique_id"
                control={control}
                placeholder={t("logistic.delivery_trip.unique_id")}
                error={Boolean(errors?.unique_id)}
                helperText={
                  errors?.unique_id && errors?.unique_id.message?.toString()
                }
                disabled
                viewMode={disabled}
                required
              />
            </Grid>
            <Grid item xs={1}>
              {!Boolean(id) && (
                <CustomizedTooltip
                  title="เรียกเลขที่เอกสารใหม่"
                  enterNextDelay={200}
                >
                  <IconButton onClick={generateUniqueId} color="primary">
                    <RestartAltOutlinedIcon />
                  </IconButton>
                </CustomizedTooltip>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Typography fontWeight={600}>
                {t("logistic.delivery_trip.reference_document_list")}
              </Typography>
            </Grid>
            <Grid item xs={11} sm={11} md={7}>
              <Box>
                {filteredRefDocument && filteredRefDocument?.length > 0
                  ? filteredRefDocument.map((reference) => (
                    <CustomizedChips
                      key={`${reference.document_id}-${reference.document_unique_id}-${reference.document_type}`}
                      onClick={() => {
                        window.open(
                          `/sales/${reference.document_type?.replace(
                            "_",
                            "-"
                          )}/${reference.document_id}`,
                          "_blank"
                        );
                      }}
                      value={reference.document_unique_id}
                      isRounded
                    />
                  ))
                  : "-"}
              </Box>
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={12} sm={12} md={4}>
              <Typography fontWeight={600}>
                {t("logistic.delivery_trip.job_no")}
              </Typography>
            </Grid>
            <Grid item xs={11} sm={11} md={7}>
              <ControlledTextField
                name="job_no"
                control={control}
                error={Boolean(errors?.job_no)}
                helperText={
                  errors?.job_no && errors?.job_no.message?.toString()
                }
                placeholder={t("logistic.delivery_trip.job_no")}
                disabled={disabled}
                viewMode={disabled}
              />
            </Grid>
            <Grid item xs={1} />
            <Grid item xs={12} sm={12} md={4}>
              <Typography fontWeight={600}>
                {t("purchase.employee_list")}
              </Typography>
            </Grid>
            <Grid item xs={11} sm={11} md={7}>
              {/* 86d37db3t — no per-status disable in Edit mode. */}
              <EmployeeListForm disabledStatus={false} />
            </Grid>
            <Grid item xs={1} />
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <Grid container spacing={1.5} justifyContent="flex-end">
            <Grid item xs={12} sm={12} md={4}>
              <ControlledDatePicker
                name="created_date"
                control={control}
                label={t("date.created_date")}
                disabled={true}
                viewMode={disabled}
                required
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <ControlledDatePicker
                name="issue_date"
                control={control}
                label={t("date.issue_date")}
                disabled={disabled || !isCancellable}
                viewMode={disabled}
                required
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <CancelConfirmation
        uniqueId={uniqueId}
        confirmation={cancelConfirmation}
        closeConfirmationHandler={closeCancelConfirmation}
        submitConfirmationHandler={submitCancelConfirmation}
      />
      <Confirmation
        title="ยืนยันหากต้องการคัดลอกเอกสาร"
        open={copyConfirmation}
        handleClose={closeCopyConfirmation}
        action={submitCopyConfirmation}
      />
      <AcceptModal
        open={paymentModal}
        handleClose={closePaymentModalHandler}
        onSubmit={validatePaymentHandler}
        documentType={SalesModelType.DeliveryTrip}
      />
    </Box>
  );
};

export default DeliveryTripHeader;
